import { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../../components/layout/Layout';
import api from '../../../api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { 
  Search, Server, Filter,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, X,
  ChevronRight as ChevronRightIcon, Link2, Cpu, MemoryStick, Monitor, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../../lib/utils';
import { useAuth } from '../../../context/AuthContext';
import { DeleteVirtDialog } from '../../../components/virt/DeleteVirtDialog';

const VirtPlatforms = () => {
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', platform_type: '' });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'host', dir: 'asc' });
  
  // Active stat filter
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  
  // Expanded platforms (for showing hosts)
  const [expandedPlatforms, setExpandedPlatforms] = useState({});
  const [platformHosts, setPlatformHosts] = useState({});
  const [loadingHosts, setLoadingHosts] = useState({});
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const colCount = isAdmin ? 10 : 9;
  const [deleteTarget, setDeleteTarget] = useState(null);

  const platformLabels = {
    vcenter: 'VMware vCenter',
    esxi: 'VMware ESXi',
  };

  const fetchPlatforms = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.platform_type) params.append('platform_type', filters.platform_type);

      const res = await api.get(`/virt/platforms?${params}`);
      setPlatforms(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar plataformas');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/virt/platforms/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch hosts for a platform
  const fetchPlatformHosts = async (platformId) => {
    if (platformHosts[platformId]) return; // Already loaded
    
    setLoadingHosts(prev => ({ ...prev, [platformId]: true }));
    try {
      const res = await api.get(`/virt/platforms/${platformId}/hosts`);
      setPlatformHosts(prev => ({ ...prev, [platformId]: res.data.hosts }));
    } catch (err) {
      toast.error('Error al cargar hosts');
    } finally {
      setLoadingHosts(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const togglePlatformExpand = (platformId) => {
    const isExpanded = expandedPlatforms[platformId];
    setExpandedPlatforms(prev => ({ ...prev, [platformId]: !isExpanded }));
    
    if (!isExpanded) {
      fetchPlatformHosts(platformId);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchPlatforms, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Stat card click handler
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    switch (filterType) {
      case 'vcenter':
        setFilters({ search: '', platform_type: 'vcenter' });
        break;
      case 'esxi':
        setFilters({ search: '', platform_type: 'esxi' });
        break;
      default:
        setFilters({ search: '', platform_type: '' });
        break;
    }
  };

  const getPlatformIcon = (type) => {
    switch (type) {
      case 'vcenter': return <Server className="h-4 w-4 text-blue-500" />;
      case 'esxi': return <Server className="h-4 w-4 text-green-500" />;
      default: return <Server className="h-4 w-4" />;
    }
  };

  const clearFilters = () => {
    setFilters({ search: '', platform_type: '' });
    setActiveStatFilter('all');
  };

  const hasFilters = filters.search || filters.platform_type;

  const formatSize = (gb) => {
    if (!gb) return '-';
    const num = Number(gb);
    if (num >= 1024) return `${(num / 1024).toFixed(1)} TB`;
    return `${num.toFixed(0)} GB`;
  };

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <th 
      className={`cursor-pointer hover:bg-muted/70 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Plataformas de Virtualización</h1>
          <p className="page-subtitle">Conexiones a vCenter y ESXi (click en fila para ver hosts)</p>
        </div>

        {/* Stats - Clickeable Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`}
              onClick={() => handleStatFilter('all')}
              data-testid="stat-total"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{stats.total || 0}</p>
                <p className="stats-card-label">Plataformas</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-blue-500/50 ${activeStatFilter === 'vcenter' ? 'border-blue-500 ring-1 ring-blue-500/20' : ''}`}
              onClick={() => handleStatFilter('vcenter')}
              data-testid="stat-vcenter"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-blue-500">{stats.vcenter_count || 0}</p>
                <p className="stats-card-label">vCenter</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-green-500/50 ${activeStatFilter === 'esxi' ? 'border-green-500 ring-1 ring-green-500/20' : ''}`}
              onClick={() => handleStatFilter('esxi')}
              data-testid="stat-esxi"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-green-500">{stats.esxi_count || 0}</p>
                <p className="stats-card-label">ESXi</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{stats.total_hosts || 0}</p>
                <p className="stats-card-label">Hosts ESXi</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{stats.total_vms || 0}</p>
                <p className="stats-card-label">VMs</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{stats.total_datastores || 0}</p>
                <p className="stats-card-label">Datastores</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros:</span>
              <div className="flex items-center gap-2 flex-1 max-w-sm">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por host o versión..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setActiveStatFilter('');
                  }}
                  className="h-9"
                  data-testid="platform-search-input"
                />
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table with Expandable Hosts */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="table-subtle" data-testid="platforms-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <th className="w-8"></th>
                  <SortableHeader field="platform" className="text-left">PLATAFORMA</SortableHeader>
                  <SortableHeader field="host" className="text-left">HOST</SortableHeader>
                  <SortableHeader field="version" className="text-left">VERSIÓN</SortableHeader>
                  <th>API</th>
                  <SortableHeader field="host_count" className="text-center justify-center">HOSTS</SortableHeader>
                  <SortableHeader field="vm_count" className="text-center justify-center">VMs</SortableHeader>
                  <SortableHeader field="datastore_count" className="text-center justify-center">DATASTORES</SortableHeader>
                  <SortableHeader field="last_scanned_at" className="text-left">ÚLTIMA SYNC</SortableHeader>
                  {isAdmin && <th className="text-center">ACCIONES</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={colCount} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : platforms.length === 0 ? (
                  <tr><td colSpan={colCount} className="text-center p-8 text-muted-foreground">No hay plataformas registradas</td></tr>
                ) : platforms.map(p => (
                  <Fragment key={p.id}>
                    <tr 
                      className={`border-b hover:bg-muted/30 cursor-pointer ${expandedPlatforms[p.id] ? 'bg-muted/20' : ''}`}
                      onClick={() => togglePlatformExpand(p.id)}
                      data-testid={`platform-row-${p.id}`}
                    >
                      <td className="p-4 text-center">
                        <ChevronRightIcon className={`h-4 w-4 transition-transform ${expandedPlatforms[p.id] ? 'rotate-90' : ''}`} />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getPlatformIcon(p.platform)}
                          <span className="font-medium">{platformLabels[p.platform] || p.platform}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <span className="font-mono text-sm">{p.host}</span>
                          {p.port && p.port !== 443 && (
                            <span className="text-muted-foreground">:{p.port}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <span className="text-sm">{p.version || '-'}</span>
                          {p.build && (
                            <p className="text-xs text-muted-foreground">Build: {p.build}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">{p.api_version || '-'}</td>
                      <td className="p-4 text-center">
                        <Badge variant="outline">{p.host_count || 0}</Badge>
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant="outline">{p.vm_count || 0}</Badge>
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant="outline">{p.datastore_count || 0}</Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {p.last_scanned_at ? formatDateARG(p.last_scanned_at) : '-'}
                      </td>
                      {isAdmin && (
                        <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => setDeleteTarget({ id: p.id, name: p.host })}
                            data-testid={`platform-delete-btn-${p.id}`}
                            title="Eliminar plataforma"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                    {/* Expanded Hosts Row */}
                    {expandedPlatforms[p.id] && (
                      <tr>
                        <td colSpan={colCount} className="bg-muted/10 p-0">
                          <div className="p-4 pl-12">
                            {loadingHosts[p.id] ? (
                              <p className="text-muted-foreground text-sm">Cargando hosts...</p>
                            ) : !platformHosts[p.id] || platformHosts[p.id].length === 0 ? (
                              <p className="text-muted-foreground text-sm">No hay hosts registrados</p>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-sm font-medium mb-3">Hosts ESXi ({platformHosts[p.id].length})</p>
                                <table className="table-subtle">
                                  <thead className="bg-muted/30">
                                    <tr className="border-b">
                                      <th>HOST IP</th>
                                      <th>CLUSTER</th>
                                      <th className="text-center">CPUs</th>
                                      <th className="text-center">RAM</th>
                                      <th className="text-center">VMs</th>
                                      <th className="text-center">ESTADO</th>
                                      <th>SERVIDOR FÍSICO</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {platformHosts[p.id].map(host => (
                                      <tr key={host.id} className="border-b border-muted/20 hover:bg-muted/20">
                                        <td className="p-2">
                                          <div className="flex items-center gap-2">
                                            <Server className="h-3 w-3 text-muted-foreground" />
                                            <span className="font-mono text-sm">{host.host_ip}</span>
                                          </div>
                                        </td>
                                        <td className="p-2 text-muted-foreground">{host.cluster_name || '-'}</td>
                                        <td className="p-2 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            <Cpu className="h-3 w-3 text-muted-foreground" />
                                            <span>{host.cpu_cores || '-'}</span>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            <MemoryStick className="h-3 w-3 text-muted-foreground" />
                                            <span>{formatSize(host.ram_gb)}</span>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            <Monitor className="h-3 w-3 text-muted-foreground" />
                                            <span>{host.vm_count || 0}</span>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center">
                                          {host.connection_state === 'connected' ? (
                                            <Badge className="bg-green-500 text-white text-xs">Online</Badge>
                                          ) : host.maintenance_mode ? (
                                            <Badge className="bg-yellow-500 text-black text-xs">Maintenance</Badge>
                                          ) : (
                                            <Badge variant="outline" className="text-xs">{host.connection_state || '-'}</Badge>
                                          )}
                                        </td>
                                        <td className="p-2">
                                          {host.server_id ? (
                                            <Link 
                                              to={`/assets/servers?search=${encodeURIComponent(host.host_ip)}`}
                                              className="flex items-center gap-1 text-blue-500 hover:underline text-sm"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <Link2 className="h-3 w-3" />
                                              {host.server_hostname}
                                            </Link>
                                          ) : (
                                            <span className="text-muted-foreground text-sm">-</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.pages} ({pagination.total} plataformas)
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        <DeleteVirtDialog
          open={!!deleteTarget}
          onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
          previewUrl={deleteTarget ? `/virt/platforms/${deleteTarget.id}/delete-preview` : null}
          deleteUrl={deleteTarget ? `/virt/platforms/${deleteTarget.id}` : null}
          onDeleted={() => { setDeleteTarget(null); fetchPlatforms(); fetchStats(); }}
        />
      </div>
    </Layout>
  );
};

export default VirtPlatforms;
